import {
  Button,
  OTPInput,
  Toast,
} from "@axismaxlife/unified-ui-components-library";
import "@fontsource/manrope";
import "@fontsource/manrope/400.css";
import { Box, Container, Stack, Typography, styled } from "@mui/material";
import CryptoJS from "crypto-js";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppToast } from "../../commanComponents/AppToast";
import { ImageLibrary } from "../../constants/ImageLibrary";
import { String } from "../../constants/Strings";
import { AgentContext } from "../../Hooks/context/AgentData";
import { OTPVerifyContext } from "../../Hooks/context/OTPVeriryContext";
import useOnboardingOTP from "../../Hooks/CustomHooks/useOnboardingOTP";
import { gaCustomEvent } from "../../logic/CommonFunctions";
import { colors, fonts } from "../../style";
import { ENCRYPTION_KEY, ENCRYPTION_IV } from "../../config/env";

const TitleTypography = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: "25px",
  fontFamily: fonts.lato,
  textAlign: "center",
  lineHeight: "27px",
  color: colors.SecondaryMidnightBlue,
}));

const SubTitleTypography = styled(Typography)(() => ({
  width: "100%",
  fontWeight: 500,
  fontSize: "14px",
  marginTop: "8px",
  marginBottom: "24px",
  fontFamily: fonts.notoSans,
}));

const Onboarding = () => {
  const [open, setOpen] = useState(false);
  const [openValidationSnackbar, setOpenValidationSnackbar] = useState(false);
  const { agentCollectData } = useContext(AgentContext);
  const navigation = useNavigate();
  const {
    errors,
    openSnackbar,
    setOpenSnackbar,
    snackbarOTPEnter,
    setSnackbarOTPEnter,
    setOtp,
    messageSet,
    handleOTPChange,
    handleSendOTP,
    handleAuthorize,
    resendOTP,
    resendEnable,
    setResendEnable,
    resendMessage,
  } = useOnboardingOTP();

  const SECRET_KEY = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
  const IV = CryptoJS.enc.Utf8.parse(ENCRYPTION_IV);

  const { collectData, verifyData } = useContext(OTPVerifyContext);
  let location = useLocation();

  const hasOtpError = Boolean(snackbarOTPEnter);
  const otpErrorMessage = String.OTPScreen.incorrectOTP;
  const handleOtpChange = (value) => {
    if (hasOtpError) setSnackbarOTPEnter(false);
    handleOTPChange(value);
  };
  const resetOtpState = () => {
    setSnackbarOTPEnter(false);
    setOtp("");
  };

  const decryptData = (encryptedData) => {
    try {
      if (!encryptedData)
        throw new Error(String.OnbordingStr.noDecryptedErrorMessage);

      const decodedData = CryptoJS.enc.Base64.parse(encryptedData);

      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: decodedData },
        SECRET_KEY,
        {
          iv: IV,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      );

      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
      if (!decryptedText)
        throw new Error(String.OnbordingStr.DecryptedErrorMessage);

      return JSON.parse(decryptedText);
    } catch (error) {
      return null;
    }
  };

  const handleClose = () => {
    setOpen(false);
    resetOtpState();
  };

  const handleResendOTP = () => {
    gaCustomEvent(
      String.gaEventName.acrLogin,
      String.gaEventClickName.acrLoginResendOtp,
      verifyData?.customerId,
    );
    resendOTP();
  };

  const handleClickOpen = async () => {
    setOpenValidationSnackbar(false);
    resetOtpState();

    const res = decryptData(location.search.match(/data=(.+)/)[1]);
    collectData(res);
    gaCustomEvent(
      String.gaEventName.acrLogin,
      String.gaEventClickName.acrLoginLetsBegin,
      res.customerId,
    );
    if (res.agentFlag) {
      agentCollectData(res);
      navigation(String.navigations.dashboard);
    }
    const data = {
      userId: res.customerId,
    };

    const success = await handleSendOTP(data);
    if (success) {
      setOpen(true);
    }
  };

  return (
    <>
      <header
        style={{
          width: "100%",
          paddingTop: "12px",
          paddingBottom: "12px",
          paddingLeft: "16px",
          backgroundColor: colors.darkBlue,
          display: "flex",
        }}
      >
        <img src={ImageLibrary.AXIS_LOGO} alt="Axis Max Life Insurance" />
      </header>
      <Container style={{ paddingBottom: "16px" }}>
        <Stack
          sx={{
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <TitleTypography style={{ marginTop: "32px" }} variant="h2">
            {String.OnbordingStr.title}
          </TitleTypography>

          <SubTitleTypography variant="subtitle2">
            {String.OnbordingStr.subtitle}
          </SubTitleTypography>
          <Box
            component="img"
            src={ImageLibrary.ONBOARDING_IMAGE}
            alt="ONBOARDING_IMAGE"
            aria-hidden="true"
            sx={{
              width: 251,
              height: 251,
              flexShrink: 0,
              marginBottom: "180px",
            }}
          />
          <Toast
            device="mobile"
            state="success"
            showIcon={false}
            closeIcon={false}
            width="100%"
            title={
              <Box
                component="span"
                sx={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <Box
                  component="img"
                  src={ImageLibrary.SAFE_SHIELD}
                  alt=""
                  aria-hidden="true"
                  sx={{ width: 24, height: 24, flexShrink: 0 }}
                />
                <Box component="span" sx={{ textAlign: "left" }}>
                  {String.OnbordingStr.badgeText}
                </Box>
              </Box>
            }
          />
          <Button
            buttonType="primary"
            width="100%"
            onClick={handleClickOpen}
            style={{ marginTop: "16px" }}
          >
            {String.OnbordingStr.ButtonName}
          </Button>
          {open && (
            <OTPInput
              className="custom-otp-modal"
              otpLength={6}
              variant="modal"
              device="mobile"
              inputType="simpleInput"
              labelContent={
                <>
                  <div className="otp-heading">{String.OTPScreen.Title}</div>
                  <label className="otp-subheading" id="otpLabel">
                    {String.OTPScreen.SubTitle("05")}
                  </label>
                </>
              }
              buttonLabel={String.OTPScreen.Authorize}
              timer={30}
              timerUnit="seconds"
              showOtpModalBtn={false}
              showModalCloseIcon={true}
              onCloseModal={handleClose}
              onChange={handleOtpChange}
              onResend={handleResendOTP}
              onButtonClick={handleAuthorize}
              error={hasOtpError}
              errorIcon={
                <img
                  src={ImageLibrary.CROSS_FILLED_RED_CIRCLE}
                  alt=""
                  aria-hidden="true"
                />
              }
              hintText={hasOtpError ? otpErrorMessage : ""}
              isDisabled={hasOtpError ? true : false}
            />
          )}
          <AppToast
            open={openSnackbar}
            handleClose={() => setOpenSnackbar(false)}
            message={messageSet.message}
            severity={String.successSeverity}
          />

          <AppToast
            open={resendEnable}
            handleClose={() => setResendEnable(false)}
            message={resendMessage}
            severity={String.successSeverity}
          />
          <AppToast
            open={openValidationSnackbar}
            handleClose={() => {
              setOpenValidationSnackbar(false);
            }}
            message={errors.invalid || errors.server}
            severity={String.ErrorSeverity}
          />
        </Stack>
      </Container>
    </>
  );
};

export default Onboarding;
